// Canvas 6: Governance and Trust Guardrails. Tier from the Risk rating of the flagged use case, four areas rated with proof, the handover design,
// one action and owner per area, then the riskiest gap and up to three launch conditions. Pulls from Canvases 1, 2, 3 and 5. Feeds 7, 8, 9.
import { makeDef, G, text, long, pick, multi, calc, fields, table, pull, note, reference } from './base.js';
import { OPT } from '../spine.js';
import { has, S, T, listOf } from '../helpers.js';

// ---------------------------------------------------------------- the tier rule (AICX working rule, a teaching simplification)
const RISK_TIER = { Low: 1, Medium: 2, High: 3 };
const MIN_CONTROLS = [3, 7, 12];
const TIERS = [
  {
    id: 't1',
    name: 'Tier 1: Risk Low',
    sub: '3 controls',
    controls: ['Named owner', 'Escalation path', 'Complaints monitored'],
  },
  {
    id: 't2',
    name: 'Tier 2: Risk Medium',
    sub: 'adds 4, so 7',
    controls: ['Plain-language explanation on request', 'Human review path', 'Fairness spot-check before launch', 'Forum approval'],
  },
  {
    id: 't3',
    name: 'Tier 3: Risk High',
    sub: 'adds 5, so 12',
    controls: [
      'Independent validation before launch',
      'Named executive owner',
      'Board visibility',
      'Incident path',
      'Human in the loop for adverse decisions',
    ],
  },
];
const AREAS = [
  {
    id: 'principles',
    label: 'Responsible AI principles',
    sub: 'Fairness, explainability and accountability: the check that has been done, or the one to do before launch.',
  },
  {
    id: 'data',
    label: 'Data governance',
    sub: 'Consent basis, data quality, lineage, privacy and security, and who signs.',
  },
  {
    id: 'handover',
    label: 'Human handover design',
    sub: 'Triggers, the three states and the context that must transfer: see Step 3.',
  },
  {
    id: 'roles',
    label: 'Roles and decision forums',
    sub: 'The executive owner, who approves, the escalation path and the AI decision forum.',
  },
];
const TRIGGERS = [
  { id: 'low', label: 'Trigger: low confidence', sub: 'When the AI is unsure, what makes it stop?' },
  { id: 'ask', label: 'Trigger: the customer asks', sub: 'What the customer says or does that must bring a person in.' },
  { id: 'sdr', label: 'Trigger: an S, D or R moment', sub: 'The sensitive-data, decision or hard-to-reverse moment from Canvas 2.' },
];
const STATES = [
  { id: 'continues', label: 'State: AI continues', sub: 'What the AI may do alone.' },
  { id: 'clarifies', label: 'State: AI clarifies', sub: 'What the AI may ask, and how many turns it has.' },
  { id: 'human', label: 'State: transfers to a human', sub: 'The point at which a person takes over, and who.' },
];
const NO_OWNER = /^(nobody|no one|none|not yet|tbc|tbd|unknown|n\/a)\b/i;

// ---------------------------------------------------------------- reference panels (the back page of the printed canvas)
const TIER_RULE = [
  ['Low', 'No sensitive data and no decision about an individual', 'Tier 1', '3: named owner; escalation path; complaints monitored'],
  [
    'Medium',
    'Sensitive data, or a decision that a person checks',
    'Tier 2',
    '7: the three above, plus plain-language explanation on request; human review path; fairness spot-check before launch; forum approval',
  ],
  [
    'High',
    "An automated decision about a customer's money, access or eligibility",
    'Tier 3',
    '12: the seven above, plus independent validation before launch; named executive owner; board visibility; incident path; ' +
      'human in the loop for adverse decisions',
  ],
];
const TIER_NOTE =
  'Tick a control only where you can name an owner and show a document. Count the controls in place for your own tier. ' +
  "Controls in proportion to risk is mainstream practice. The numbers of controls are the course's own; no regulator prescribes them.";
const RATING_RULE = [
  ['In place', 'You can name an owner and show a document'],
  ['Partial', 'Intent or a first version'],
  ['Not yet', 'Neither'],
];
const AREA_KEY = [
  ['Responsible AI principles', 'Fairness, explainability and accountability: the check that has been done, or the one to do before launch'],
  [
    'Data governance',
    'Four questions. On what basis do we use this personal data, and is that written down? How good is it for this use? ' +
      'From where does it come, source to decision? Who signs, including that it is kept private and secure?',
  ],
  ['Human handover design', 'The triggers, the three states and the context that must transfer (Step 3)'],
  [
    'Roles and decision forums',
    'The executive owner, who approves, the escalation path and the AI decision forum. Your People and Governance level and proof are on Canvas 1.',
  ],
];
const HANDOVER_KEY = [
  [
    'Three triggers',
    'Low confidence: the AI is unsure of its own answer',
    'The customer asks for a person',
    'An S, D or R moment',
  ],
  [
    'Three states',
    'AI continues: what the AI may do alone',
    'AI clarifies: what it may ask, and for how many turns',
    'Transfers to a human: at what point, and to which role',
  ],
  [
    'Context that must transfer',
    'Who the customer is',
    'What was decided, and when',
    'What the AI already said; what the customer asked for; what is already confirmed',
  ],
];
const HANDOVER_NOTE =
  'One rule on top: before it hands over, the AI says what it cannot do. It does not go quiet. The triggers become the rehearsals on Canvas 7.';
const STEP45_KEY = [
  ['Step 4', 'One action and one owner (a role) for each area rated Partial or Not yet. An area rated In place needs a named owner.'],
  [
    'Step 5',
    'The riskiest gap is the one guardrail whose absence would stop your launch. Name its owner outside the room: a role, and not someone ' +
      'at your table. "Nobody yet" is a valid answer and a finding. Then write up to three launch conditions.',
  ],
  [
    'The guidance map',
    'For sceptic question 3, use the guidance map handout: name a source, its status (law, guidance or a proposal) and a role in your ' +
      "organisation that has checked it. The map reports each source's status; it does not say what the law requires. Guidance, not legal advice.",
  ],
];
const SPINE_KEY = [
  [
    'Check 1, the Spine check',
    'The riskiest trust moment on Canvas 5 has the most S, D, R ticks on Canvas 2, and the tier matches the Canvas 3 Risk rating. ' +
      'A moment that ticks both D and R should not sit under a Low or Medium rating. Pass or Go back. If it says Go back, correct ' +
      'Canvas 2, 3 or 5. Never adjust Canvas 6 to fit.',
  ],
];

// ---------------------------------------------------------------- local helpers
const fx = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};
const norm = (s) => String(s || '').trim().toLowerCase();
const tierOf = (risk) => RISK_TIER[risk] || null;
const tierText = (t) => (t ? `Tier ${t}: ${MIN_CONTROLS[t - 1]} minimum controls` : '');
const ownerNamed = (s) => has(s) && !NO_OWNER.test(String(s).trim());
// facts are sentences for later canvases: a capital letter and a full stop
const sentence = (s) => {
  const t = String(s || '').trim();
  if (!t) return '';
  const c = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(c) ? c : `${c}.`;
};

// The use case this canvas covers: the guardrail-flagged use case among the tested three (best Total, then Value, then Feasibility),
// and with no flag the top pick. This is the workbook's rule (Lists AG5).
function flaggedCase(up) {
  const f = fx(up, 3);
  const list = (f.candidates || []).filter((c) => c && c.flag && has(c.name));
  list.sort((a, b) => (b.total || 0) - (a.total || 0) || (b.value || 0) - (a.value || 0) || (b.feas || 0) - (a.feas || 0) || (a.n || 0) - (b.n || 0));
  if (list.length) return { ...list[0], flagged: true };
  const top = f.topPick;
  return top && has(top.name) ? { ...top, flagged: false } : null;
}

// the S, D, R ticks Canvas 2 holds for a stage (matched by the stage name, as the workbook does)
function stageTicks(up, stage) {
  const st = (fx(up, 2).stages || []).find((s) => norm(s.stage) === norm(stage));
  return st ? { name: st.stage, sdr: st.sdr || [] } : null;
}

// Check 1, the Spine check (workbook 06 Guardrails C16). Returns { result: 'Pass' | 'Go back' | '', why }.
function spineCheck(up, uc, tier) {
  const r = fx(up, 5).riskiest;
  if (!tier || !r || !has(r.stage)) return { result: '', why: '' };
  const stages = fx(up, 2).stages || [];
  const max = stages.reduce((m, s) => Math.max(m, (s.sdr || []).length), 0);
  const there = stages.find((s) => norm(s.stage) === norm(r.stage));
  if (!there || max === 0 || (there.sdr || []).length !== max) {
    return { result: 'Go back', why: 'Go back to Canvas 5. The riskiest trust moment there does not have the most ticks on Canvas 2.' };
  }
  const t = stageTicks(up, uc && uc.stage);
  if (t && t.sdr.includes('D') && t.sdr.includes('R') && tier < 3) {
    return { result: 'Go back', why: `Go back to Canvas 2 or 3. This moment ticks D and R, but the use case is rated ${uc.risk}.` };
  }
  return { result: 'Pass', why: '' };
}

const tierItemGet = (up) => tierText(tierOf((flaggedCase(up) || {}).risk)) || null;

// ---------------------------------------------------------------- NorthStar (master spec section 6; the printed canvas)
const NORTHSTAR = {
  'ctl.t1': 'Complaints monitored',
  'ctl.t3': 'Human in the loop for adverse decisions',
  ...T(
    'area',
    [
      { rating: 'Partial', proof: 'Plain-language reason drafted; no fairness check yet' },
      { rating: 'Not yet', proof: 'No consent basis or lineage written down' },
      { rating: 'Partial', proof: 'An officer calls on declines; triggers drafted today, not yet approved' },
      { rating: 'Not yet', proof: 'No AI decision forum; no executive owner' },
    ],
    ['principles', 'data', 'handover', 'roles'],
  ),
  ...T(
    'trig',
    [
      { text: 'The AI is not confident in the reason it would give' },
      { text: 'The customer asks for a person' },
      { text: 'The decline itself (D and R on Canvas 2)' },
    ],
    ['low', 'ask', 'sdr'],
  ),
  ...T(
    'state',
    [
      { text: 'While confidence is high and no trigger fires' },
      { text: 'Up to 3 turns to confirm what the customer needs' },
      { text: 'To the service officer, with the context that must transfer' },
    ],
    ['continues', 'clarifies', 'human'],
  ),
  'ho.context':
    'Who the customer is and how long they have banked with NorthStar; what was declined and when; what the AI already said; ' +
    'what the customer asked for; what is already confirmed',
  ...T(
    'act',
    [
      { action: 'Run the training-population check before launch', owner: 'Head of IT and Data' },
      { action: 'Write the consent basis and the data lineage', owner: 'Head of IT and Data' },
      { action: 'Approve the triggers and what must transfer', owner: 'Head of Retail Banking' },
      { action: 'Name the executive owner and set up the forum', owner: 'Head of Retail Banking' },
    ],
    ['principles', 'data', 'handover', 'roles'],
  ),
  'gap.text': 'Nobody owns explanation and appeal',
  'gap.owner': 'Head of Risk',
  'cond.1': 'A plain-language reason with every credit decline',
  'cond.2': 'A human review path for declines',
  'cond.3': "A check that the training population resembles NorthStar's customers",
  'so.reviewer': 'Risk and Compliance',
  'so.objection': 'Who owns explanation and appeal, and what would I have to sign?',
  'so.evidence': 'Riskiest gap and launch conditions: Canvas 6',
  'so.owner': 'Head of Risk',
  'so.status': 'Open',
  'so.star': 'Yes',
};

export default makeDef(6, {
  short: 'Guardrails',
  purpose: 'Decide which guardrails your flagged use case must have in place, rate where you stand in four areas, and name who owns the gap.',
  howTo: [
    'Build this canvas for your flagged use case. Its Risk rating from Canvas 3 sets the tier and the minimum number of controls.',
    'Tick a control, or rate an area In place, only where you can name an owner and show a document. A rating with no proof does not count.',
    'Design the handover, then write one action and an owner (a role) for each area that is not In place.',
    'Name the riskiest gap and an owner outside the room, then up to three launch conditions. They travel on to Canvases 7, 8 and 9.',
  ],
  results: [
    { id: 'tier', label: 'Tier', rule: 'From Step 1: the Risk rating sets the tier.' },
    { id: 'inPlace', label: 'In place', rule: 'Count the areas rated In place in Step 2.' },
    { id: 'partial', label: 'Partial', rule: 'Count the areas rated Partial in Step 2.' },
    { id: 'notYet', label: 'Not yet', rule: 'Count the areas rated Not yet in Step 2.' },
    { id: 'controls', label: 'Controls in place', rule: 'From Step 1: your ticks, of 3, 7 or 12.' },
    { id: 'gapOwner', label: 'Riskiest gap has an owner outside the room', rule: 'From Step 5: Yes when you name a role outside the room.' },
    { id: 'spine', label: 'Check 1: Spine', rule: 'Pass when the riskiest trust moment has the most ticks and the tier matches the Risk rating.' },
  ],
  steps: [
    {
      hint:
        'Build this canvas for your flagged use case; with no flag, use your top pick. The Risk rating sets the tier: Low is Tier 1, Medium Tier 2, High Tier 3. ' +
        'Then tick the controls you can show.',
      blocks: [
        pull('p1', 'From your earlier canvases', [
          {
            id: 'case',
            label: 'Use case tested here',
            ref: 'Canvas 3',
            no: 3,
            get: (up) => {
              const c = flaggedCase(up);
              if (!c) return null;
              return `${c.name}${c.flagged ? ' (guardrail flag)' : ' (the top pick: no flag among the top three)'}`;
            },
            note: 'Your flagged use case. With no flag, your top pick.',
          },
          { id: 'risk', label: 'Risk rating', ref: 'Canvas 3', no: 3, get: (up) => (flaggedCase(up) || {}).risk || null },
          { id: 'tier', label: 'Tier and minimum controls', ref: 'Canvas 3 Risk rating', no: 3, get: tierItemGet },
          {
            id: 'sdr',
            label: 'S, D, R ticks at its stage',
            ref: 'Canvas 2',
            no: 2,
            get: (up) => {
              const c = flaggedCase(up);
              const t = c && stageTicks(up, c.stage);
              return t ? `${t.sdr.length ? t.sdr.join(', ') : 'none'} at ${t.name}` : null;
            },
          },
          {
            id: 'moment',
            label: 'Riskiest trust moment',
            ref: 'Canvas 5',
            no: 5,
            get: (up) => {
              const r = fx(up, 5).riskiest;
              if (!r || !has(r.stage)) return null;
              return `${r.stage}${r.sdr && r.sdr.length ? ` (${r.sdr.join(', ')})` : ''}`;
            },
            note: 'Check 1 compares it with the ticks on Canvas 2.',
          },
          { id: 'involve', label: 'Human involvement', ref: 'Canvas 5', no: 5, get: (up) => fx(up, 5).involvement || null },
        ]),
        fields([
          multi(
            'ctl.t1',
            'Tier 1: Risk Low (3 controls)',
            TIERS[0].controls,
            {
              w: 4,
              guide: G(
                'Tick each control you can name an owner for and show a document for. Leave the rest blank.',
                'Complaints monitored',
                'Tick a control only where you can name an owner and show a document.',
                'Ticking a control because it is planned. A plan is Partial at best, and Partial does not tick.',
              ),
            },
          ),
          multi(
            'ctl.t2',
            'Tier 2: Risk Medium (adds 4, so 7)',
            TIERS[1].controls,
            {
              w: 4,
              guide: G(
                'Tick each control you can show. Controls above your tier are not required, and they do not count.',
                'None ticked in the NorthStar example.',
                'Tier 2 applies to a Medium rating, and a High rating includes it.',
                'Ticking controls above your tier to lift the count.',
              ),
            },
          ),
          multi(
            'ctl.t3',
            'Tier 3: Risk High (adds 5, so 12)',
            TIERS[2].controls,
            {
              w: 4,
              guide: G(
                'Tick each control you can show. They count only when your tier is 3.',
                'Human in the loop for adverse decisions',
                'Tier 3 applies to a High rating: an automated decision about a customer\'s money, access or eligibility.',
                'Ticking independent validation before anyone outside the team has validated the model.',
              ),
            },
          ),
          calc('ctl_count', 'Controls in place', {
            w: 2,
            rule: 'your ticks, of 3, 7 or 12',
            guide: G('Counted for you: the ticks in your own tier and the tiers below it.', '2 of 12', 'The minimum is 3, 7 or 12 for Tier 1, 2 or 3.'),
          }),
          calc('spine', 'Check 1: Spine check', {
            w: 2,
            rule: 'Pass when the riskiest trust moment has the most ticks on Canvas 2 and the tier matches the Risk rating',
            guide: G(
              'Worked out for you: Pass or Go back.',
              'Pass',
              'A moment that ticks both D and R is rated High. If it says Go back, correct Canvas 2, 3 or 5.',
              'Adjusting Canvas 6 to make it pass. Fix the source canvas.',
            ),
          }),
        ]),
        note('Controls above your tier are not required and do not count. The tiers are a teaching simplification. Guidance, not legal advice.', 'rule'),
        reference('The tier rule (AICX working rule, a teaching simplification)', ['Canvas 3 Risk rating', 'It means', 'Tier', 'Minimum controls'], TIER_RULE),
        note(TIER_NOTE),
        reference('Check 1, the Spine check', ['Check', 'Rule'], SPINE_KEY),
      ],
    },
    {
      hint: 'A rating with no proof does not count. In place: you can name an owner and show a document. Partial: intent or a first version. Not yet: neither.',
      blocks: [
        pull('p2', 'From Canvas 1', [
          {
            id: 'people',
            label: 'People and Governance',
            ref: 'Canvas 1',
            no: 1,
            get: (up) => {
              const p = (fx(up, 1).dims || {}).people;
              if (!p || p.level === null || p.level === undefined || p.level === '') return null;
              return `Level ${p.level}${p.levelName ? `, ${p.levelName}` : ''}${p.proof ? `: ${p.proof}` : ''}`;
            },
            note: 'Use it as evidence for the Roles and decision forums row.',
          },
        ]),
        table('area', {
          rowLabel: 'Area',
          rows: { fixed: AREAS },
          pdf: 'cards',
          columns: [
            pick('rating', 'Rating', OPT.rating, {
              required: true,
              w: 2,
              guide: G(
                'Pick the rating you can back with proof. In place needs an owner and a document. Partial is intent or a first version. Not yet is neither.',
                'Partial for Responsible AI principles; Not yet for Data governance.',
                'In place: you can name an owner and show a document. Partial: intent or a first version. Not yet: neither.',
                'Rating the intention. A rating with no proof does not count.',
              ),
            }),
            long('proof', 'Proof', {
              required: true,
              w: 4,
              rows: 2,
              placeholder: 'The document, decision or fact behind the rating',
              guide: G(
                'Write the document, decision or fact that backs the rating. For Not yet, write what is missing.',
                'Plain-language reason drafted; no fairness check yet',
                undefined,
                'An opinion is not proof. Name something a reviewer could ask to see.',
              ),
            }),
          ],
        }),
        note('Write the proof beside the rating. A rating with no proof does not count.', 'rule'),
        reference('The rating rule (AICX working rule)', ['Rating', 'It means'], RATING_RULE),
        reference('The four areas: what the proof is about', ['Area', 'What the proof is about'], AREA_KEY),
      ],
    },
    {
      hint:
        'Write three triggers, three states and what must transfer: what the person must already know when they take over, ' +
        'so the customer does not repeat themselves.',
      blocks: [
        table('trig', {
          title: 'Three triggers',
          rowLabel: 'Trigger',
          rows: { fixed: TRIGGERS },
          pdf: 'cards',
          columns: [
            long('text', 'What makes a person step in', {
              required: true,
              w: 4,
              rows: 2,
              guide: G(
                'Write what makes the AI stop and bring a person in, in words the build team could test.',
                'Low confidence: the AI is not confident in the reason it would give. The customer asks: the customer asks for a person. ' +
                  'An S, D or R moment: the decline itself (D and R on Canvas 2).',
                undefined,
                'A vague trigger such as "when needed". Say what the AI or the customer does that fires it.',
              ),
            }),
          ],
        }),
        table('state', {
          title: 'Three states',
          rowLabel: 'State',
          rows: { fixed: STATES },
          pdf: 'cards',
          columns: [
            long('text', 'What happens in this state', {
              required: true,
              w: 4,
              rows: 2,
              guide: G(
                'Say what the AI may do alone, what it may ask and for how many turns, and the point at which a person takes over, and who.',
                'AI continues: while confidence is high and no trigger fires. AI clarifies: up to 3 turns to confirm what the customer needs. ' +
                  'Transfers to a human: to the service officer, with the context that must transfer.',
                undefined,
                'Letting the AI go quiet before it hands over. It says what it cannot do first.',
              ),
            }),
          ],
        }),
        fields([
          long('ho.context', 'Context that must transfer', {
            required: true,
            w: 4,
            rows: 3,
            guide: G(
              'Write what the person must already know when they take over, so the customer does not repeat themselves.',
              'Who the customer is and how long they have banked with NorthStar; what was declined and when; what the AI already said; ' +
                'what the customer asked for; what is already confirmed',
              undefined,
              'Leaving it to the person to ask again. The customer then repeats everything.',
            ),
          }),
        ]),
        note(HANDOVER_NOTE, 'rule'),
        reference('Handover design (AICX working rule; no published standard defines the triggers or the states)', ['Part', 'First', 'Second', 'Third'], HANDOVER_KEY),
      ],
    },
    {
      hint: 'Write one action and an owner (a role) for each area rated Partial or Not yet. An area rated In place needs a named owner.',
      blocks: [
        table('act', {
          rowLabel: 'Area',
          rows: { fixed: AREAS.map((a) => ({ id: a.id, label: a.label })) },
          pdf: 'table',
          columns: [
            text('action', 'One action', {
              required: ({ v, rowInfo }) => ['Partial', 'Not yet'].includes(S(v, `area.${rowInfo.id}.rating`)),
              w: 4,
              guide: G(
                'Write the one action that would move this area up a rating. Do it for each area rated Partial or Not yet.',
                'Run the training-population check before launch',
                undefined,
                'Two actions in one cell. Choose the one that matters most.',
              ),
            }),
            text('owner', 'Owner (a role)', {
              required: ({ v, rowInfo }) => has(v[`area.${rowInfo.id}.rating`]),
              w: 2,
              guide: G(
                'A role, not a name. An area rated In place needs a named owner too.',
                'Head of IT and Data',
                undefined,
                'Naming someone at your table. Name the role that will still hold the job next year.',
              ),
            }),
          ],
        }),
        reference('Steps 4 and 5, and the guidance map', ['Step', 'What to do'], STEP45_KEY),
      ],
    },
    {
      hint:
        'Name the one guardrail whose absence would stop the launch, and a role outside this room who owns it. Then write up to three launch conditions.',
      blocks: [
        fields([
          text('gap.text', 'Riskiest gap: the one guardrail whose absence would stop the launch', {
            required: true,
            w: 4,
            guide: G(
              'Name the one guardrail whose absence would stop the launch. Choose it from the areas you rated Partial or Not yet.',
              'Nobody owns explanation and appeal',
              undefined,
              'A list of gaps. Choose the one that would stop the launch.',
            ),
          }),
          text('gap.owner', 'Owner outside the room (a role, not someone at this table)', {
            w: 4,
            placeholder: 'A role. "Nobody yet" is a valid answer and a finding',
            guide: G(
              'A role outside this room who can close the gap or accept the risk. "Nobody yet" is a valid answer and a finding.',
              'Head of Risk',
              'The tile reads Yes only when you name a role outside the room.',
              'Naming someone at your table. The owner must be outside the room.',
            ),
          }),
          text('cond.1', 'Launch condition 1', {
            required: true,
            w: 4,
            guide: G(
              'Write a condition that must be true before launch, in words a gate could test.',
              'A plain-language reason with every credit decline',
              undefined,
              'A wish such as "good governance". A condition is something a decider can check.',
            ),
          }),
          text('cond.2', 'Launch condition 2', {
            w: 4,
            guide: G('A second condition, if there is one. Up to three.', 'A human review path for declines'),
          }),
          text('cond.3', 'Launch condition 3', {
            w: 4,
            guide: G('A third condition, if there is one.', "A check that the training population resembles NorthStar's customers"),
          }),
        ]),
        note('Launch conditions go to the gates on Canvas 8 and to the one-pager on Canvas 9.'),
      ],
    },
  ],
  derive(v, up) {
    const uc = flaggedCase(up);
    const tier = tierOf(uc && uc.risk);
    const min = tier ? MIN_CONTROLS[tier - 1] : null;
    const touched = Object.keys(v || {}).some((k) => !k.startsWith('_') && !k.startsWith('so.') && has(v[k]));
    const out = { calc: {}, results: {}, evidence: [], warnings: [], blockers: [] };

    // step 1: controls in place (ticks above the tier are not counted, as in the workbook)
    const ticks = TIERS.map((t) => listOf(S(v, `ctl.${t.id}`)).filter((c) => t.controls.includes(c)));
    const counted = tier ? ticks.slice(0, tier).reduce((a, l) => a + l.length, 0) : null;
    const above = tier ? ticks.slice(tier).reduce((a, l) => a + l.length, 0) : 0;
    const controls = tier && touched ? `${counted} of ${min}` : '';
    const spine = spineCheck(up, uc, tier);

    // step 2 and 4: the four areas
    const areas = AREAS.map((a) => ({
      key: a.id,
      label: a.label,
      rating: S(v, `area.${a.id}.rating`),
      proof: S(v, `area.${a.id}.proof`),
      action: S(v, `act.${a.id}.action`),
      owner: S(v, `act.${a.id}.owner`),
    }));
    const rated = areas.filter((a) => a.rating).length;
    const count = (r) => areas.filter((a) => a.rating === r).length;
    const tally = rated
      ? { inPlace: count('In place'), partial: count('Partial'), notYet: count('Not yet') }
      : { inPlace: null, partial: null, notYet: null };

    // step 3 and 5
    const triggers = TRIGGERS.map((t) => S(v, `trig.${t.id}.text`));
    const states = STATES.map((s) => S(v, `state.${s.id}.text`));
    const gap = S(v, 'gap.text');
    const gapOwner = S(v, 'gap.owner');
    const named = ownerNamed(gapOwner);
    const conds = [1, 2, 3].map((n) => S(v, `cond.${n}`)).filter(Boolean);

    out.calc.ctl_count = controls;
    out.calc.spine = spine.result;
    out.results = {
      tier: tierText(tier),
      inPlace: rated ? String(tally.inPlace) : '',
      partial: rated ? String(tally.partial) : '',
      notYet: rated ? String(tally.notYet) : '',
      controls,
      gapOwner: has(gap) ? (named ? 'Yes' : 'No') : '',
      spine: spine.result,
    };

    if (!tier) {
      out.blockers.push({ step: 's1', text: 'This canvas needs the Risk rating of your flagged use case, or of your top pick. Finish Canvas 3 first.' });
    }
    if (spine.why) out.warnings.push({ step: 's1', text: `${spine.why} Never adjust Canvas 6 to fit.` });
    if (above > 0) {
      out.warnings.push({ step: 's1', text: `${above} ${above === 1 ? 'control is' : 'controls are'} ticked above Tier ${tier}. Controls above your tier are not counted.` });
    }
    if (has(gap) && !named) {
      out.warnings.push({
        step: 's5',
        text: 'Name an owner outside the room: a role, not someone at this table. "Nobody yet" is a valid finding, and it stays on the Ledger.',
      });
    }

    out.facts = {
      useCase: uc ? uc.name : '',
      risk: uc && uc.risk ? uc.risk : '',
      tier,
      minControls: min,
      inPlace: tier && touched ? counted : null,
      areas,
      tally,
      riskiestGap: sentence(gap),
      gapOwner: named ? gapOwner : '',
      launchConditions: conds.map(sentence),
      // extras for later canvases
      flagged: !!(uc && uc.flagged),
      useCaseStage: uc && uc.stage ? uc.stage : '',
      spine: spine.result,
      triggers,
      states,
      context: S(v, 'ho.context'),
    };
    return out;
  },
  NORTHSTAR,
});
